import { useRef, useCallback, useState } from 'react';
import { Box, Slider, Stack, IconButton, Typography, Tooltip } from '@mui/material';
import Iconify from '../../../components/iconify';
import { useTimeline } from './TimelineContext';

function formatDuration(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatClockTime(epochMs) {
  return new Date(epochMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatFullDateTime(epochMs) {
  const d = new Date(epochMs);
  return `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}  ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

export default function UnifiedTimeline({
  recordings,
  eventsMap = {},
  globalMarkers = [],
  tabMarkers = [],
  activeTabId,
  allTabMarkers = [], // [{ tabId, tabName, markers[] }]
  onSwitchToTab,
  recordingMetadata = {},
  // Legacy prop
  timelineMarkers,
}) {
  const { currentTime, isPlaying, seek, play, pause, bounds, window: timeWindow, setWindow, clearWindow } = useTimeline();
  const sliderRef = useRef(null);
  const barRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [timeMode, setTimeMode] = useState('clock');
  const [showAllTabMarkers, setShowAllTabMarkers] = useState(false);

  const duration = bounds.end - bounds.start;
  const elapsed = currentTime - bounds.start;
  const allEvents = Object.values(eventsMap).flat();

  // Use legacy prop if new props not provided
  const effectiveGlobalMarkers = globalMarkers.length > 0 ? globalMarkers : (timelineMarkers || []);
  const effectiveTabMarkers = tabMarkers || [];

  // Collect other-tab markers when show-all is enabled
  const otherTabMarkers = showAllTabMarkers
    ? allTabMarkers
        .filter((t) => t.tabId !== activeTabId)
        .flatMap((t) => t.markers.map((m) => ({ ...m, tabId: t.tabId, tabName: t.tabName })))
    : [];

  // Current playhead position as percentage
  const playheadPct = duration > 0 ? ((currentTime - bounds.start) / duration) * 100 : 0;

  const handleSliderChange = useCallback((_, value) => {
    seek(value);
  }, [seek]);

  const posToTime = useCallback((clientX) => {
    if (!barRef.current) return bounds.start;
    const rect = barRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return bounds.start + pct * duration;
  }, [bounds, duration]);

  // Window selection temporarily disabled
  // const handleBarMouseDown = useCallback((e) => {
  //   if (e.button !== 0) return;
  //   const time = posToTime(e.clientX);
  //   setWindow({ start: time, end: time });
  //   setDragging('end');
  //   const handleMove = (me) => {
  //     const t = posToTime(me.clientX);
  //     setWindow((prev) => {
  //       if (!prev) return { start: time, end: t };
  //       return { start: Math.min(time, t), end: Math.max(time, t) };
  //     });
  //   };
  //   const handleUp = () => {
  //     setDragging(null);
  //     document.removeEventListener('mousemove', handleMove);
  //     document.removeEventListener('mouseup', handleUp);
  //     setWindow((prev) => {
  //       if (!prev || prev.end - prev.start < 1000) return null;
  //       return prev;
  //     });
  //   };
  //   document.addEventListener('mousemove', handleMove);
  //   document.addEventListener('mouseup', handleUp);
  // }, [posToTime, setWindow]);

  const windowLeft = timeWindow && duration > 0 ? ((timeWindow.start - bounds.start) / duration) * 100 : 0;
  const windowWidth = timeWindow && duration > 0 ? ((timeWindow.end - timeWindow.start) / duration) * 100 : 0;
  const windowDuration = timeWindow ? timeWindow.end - timeWindow.start : duration;

  const renderMarkerPin = (marker, i, { opacity = 1, outlined = false, onClick, tooltipExtra = '' }) => {
    const left = duration > 0 ? ((marker.time - bounds.start) / duration) * 100 : 0;
    const color = marker.color || '#1976d2';
    const tooltipText = `${tooltipExtra}${marker.text || marker.label}`;

    return (
      <Tooltip key={`marker-${i}-${opacity}`} title={tooltipText} arrow placement="top">
        <Box
          sx={{
            position: 'absolute', left: `${left}%`, top: 0,
            width: 2, height: '100%', bgcolor: color, zIndex: 4,
            cursor: 'pointer', pointerEvents: 'auto',
            opacity,
            '&::after': {
              content: '""', position: 'absolute', top: -4, left: -3,
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: outlined ? 'transparent' : color,
              border: outlined ? `2px solid ${color}` : 'none',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (onClick) {
              onClick();
            } else {
              seek(marker.time);
            }
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {/* Play/Pause button */}
        <IconButton onClick={isPlaying ? pause : play} size="medium">
          <Iconify icon={isPlaying ? 'eva:pause-circle-fill' : 'eva:play-circle-fill'} width={40} />
        </IconButton>

        {/* Start time label */}
        <Tooltip title="Click to toggle: clock / elapsed" placement="top">
          <Typography
            variant="caption"
            sx={{ minWidth: timeMode === 'clock' ? 80 : 36, fontFamily: 'monospace', cursor: 'pointer', userSelect: 'none', textAlign: 'center', '&:hover': { color: 'primary.main' } }}
            onClick={() => setTimeMode((m) => m === 'clock' ? 'elapsed' : 'clock')}
          >
            {timeMode === 'clock' ? formatClockTime(currentTime) : formatDuration(elapsed)}
          </Typography>
        </Tooltip>

        {/* Center column: colored bar stacked above slider */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Recording segments bar */}
          <Box
            ref={barRef}
            sx={{
              position: 'relative', height: 24, bgcolor: 'grey.100', borderRadius: 1,
              userSelect: 'none', mb: 0.5,
            }}
          >
            {/* Recording segments */}
            {recordings.map((rec) => {
              const left = duration > 0 ? ((rec.startTime - bounds.start) / duration) * 100 : 0;
              const width = duration > 0 ? ((rec.endTime - rec.startTime) / duration) * 100 : 0;
              const meta = recordingMetadata[rec.id];
              const color = meta?.color || '#1976d2';
              const displayName = meta?.label || rec.name;
              return (
                <Box
                  key={rec.id}
                  sx={{
                    position: 'absolute', left: `${left}%`, width: `${width}%`,
                    height: '100%', bgcolor: color, borderRadius: 1, opacity: 0.6,
                    pointerEvents: 'none',
                  }}
                  title={displayName}
                />
              );
            })}

            {/* Event markers */}
            {allEvents.map((evt, i) => {
              const left = duration > 0 ? ((evt.startTime - bounds.start) / duration) * 100 : 0;
              const width = duration > 0 ? (Math.max(evt.endTime - evt.startTime, duration * 0.003) / duration) * 100 : 0;
              return (
                <Tooltip key={`evt-${i}`} title={evt.label} arrow placement="top">
                  <Box
                    sx={{
                      position: 'absolute', left: `${left}%`, width: `${Math.max(width, 0.3)}%`,
                      height: '100%', bgcolor: 'error.main', opacity: 0.8, zIndex: 2, pointerEvents: 'none',
                    }}
                  />
                </Tooltip>
              );
            })}

            {/* Global markers */}
            {effectiveGlobalMarkers.map((marker, i) =>
              renderMarkerPin(marker, `global-${i}`, { opacity: 1, outlined: false })
            )}

            {/* Current tab markers */}
            {effectiveTabMarkers.map((marker, i) =>
              renderMarkerPin(marker, `tab-${i}`, { opacity: 1, outlined: false })
            )}

            {/* Other-tab markers */}
            {otherTabMarkers.map((marker, i) =>
              renderMarkerPin(marker, `other-${i}`, {
                opacity: 0.4,
                outlined: true,
                tooltipExtra: `[${marker.tabName}] `,
                onClick: () => {
                  seek(marker.time);
                  onSwitchToTab?.(marker.tabId);
                },
              })
            )}

            {/* Playhead indicator */}
            <Box
              sx={{
                position: 'absolute',
                left: `${playheadPct}%`,
                top: -2,
                width: 0,
                height: 'calc(100% + 4px)',
                zIndex: 6,
                pointerEvents: 'none',
                // Vertical line
                borderLeft: '2px solid',
                borderColor: 'primary.main',
                // Triangle head
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -4,
                  left: -5,
                  width: 0,
                  height: 0,
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderTop: '5px solid',
                  borderTopColor: 'primary.main',
                },
              }}
            />

            {/* Window selection overlay — temporarily disabled */}
          </Box>

          {/* Slider — aligned directly below the colored bar */}
          <Slider
            ref={sliderRef}
            value={currentTime}
            min={bounds.start}
            max={bounds.end}
            step={Math.max(1, Math.floor(duration / 10000))}
            onChange={handleSliderChange}
            size="small"
            sx={{ p: 0 }}
          />
        </Box>

        {/* End time label */}
        <Tooltip title="Click to toggle: clock / elapsed" placement="top">
          <Typography
            variant="caption"
            sx={{ minWidth: timeMode === 'clock' ? 80 : 36, fontFamily: 'monospace', cursor: 'pointer', userSelect: 'none', textAlign: 'center', '&:hover': { color: 'primary.main' } }}
            onClick={() => setTimeMode((m) => m === 'clock' ? 'elapsed' : 'clock')}
          >
            {timeMode === 'clock' ? formatClockTime(bounds.end) : formatDuration(duration)}
          </Typography>
        </Tooltip>

        {/* Show all tab markers toggle */}
        <Tooltip title={showAllTabMarkers ? 'Hide other tabs\' markers' : 'Show all tabs\' markers'} placement="top">
          <IconButton
            size="small"
            onClick={() => setShowAllTabMarkers((v) => !v)}
            color={showAllTabMarkers ? 'primary' : 'default'}
          >
            <Iconify icon={showAllTabMarkers ? 'eva:eye-fill' : 'eva:eye-off-fill'} width={18} />
          </IconButton>
        </Tooltip>

        {/* Window info — temporarily disabled */}
      </Stack>

      {/* Full date + time display */}
      <Typography variant="h4" sx={{ textAlign: 'center', fontFamily: 'monospace', color: 'text.primary', py: 0.5 }}>
        {formatFullDateTime(currentTime)}
      </Typography>
    </Box>
  );
}
